<template>
  <div :class="$style.flexCol">
    <div :class="$style.container">
      <div :class="$style.dropDownsColumn">
        <DropDown
          v-validate="'required'"
          attribute="scimAttribute"
          :class="$style.dropDown"
          :disabled="listItem.readOnly"
          headerText="Select attribute"
          name="SCIM attribute"
          :options="unusedScimAttributes"
          :style="isSCIMAttributeDropdownClickable"
          :value="scimAttribute"
          @input="handleAttributeChange"
        />
      </div>
      <div :class="$style.dropDownsColumn">
        <DropDown
          v-validate="isJcAttributeRequired"
          attribute="jcUiValue"
          :class="$style.dropDown"
          :disabled="listItem.readOnly"
          headerText="Select attribute"
          name="JumpCloud attribute"
          :options="getUIAttributeNames"
          :style="isJCAttributeDropdownClickable"
          :value="jcUiValue"
          @input="handleAttributeChange"
        />
      </div>
      <div :class="$style.toggleButtonsColumn">
        <ScimRadioButtonGroup
          :handleOptionChange="handleAttributeChange"
          :options="scimAttributeExclusionRadioGroup"
          :state="exclude"
          :usePrettyToggles="true"
        />
      </div>
    </div>
    <p
      v-if="listItem.target === PASSWORD_TR"
      :class="$style.note"
      data-test-id="note"
    >
      <strong>NOTE:</strong>
      If the password attribute is included, the password will be sent in accordance with the SCIM
      specification and will be sent over TLS.  Only enable this option if you are not using SSO or
      the service provider requires a password change and you are comfortable with your service
      provider's handling of your sensitive information.
    </p>
  </div>
</template>

<script>
import { NOT_APPLICABLE, PASSWORD_TR, scimCoreAttributes } from 'client/util/Constants/ApplicationConstants';
import { isMatch } from 'lodash';
import { mapState } from 'vuex';
import DropDown from 'components/FormValidation/DropDown';
import IdentityManagementMixin from 'client/applications/mixins/IdentityManagementMixin';
import ScimRadioButtonGroup from 'client/applications/scim/ScimRadioButtonGroup';

const scimAttributeExclusionRadioGroup = [
  { text: 'include', value: false },
  { text: 'exclude', value: true },
];

export default {

  name: 'ScimConnectorAttributeField',

  components: {
    DropDown,
    ScimRadioButtonGroup,
  },

  mixins: [
    IdentityManagementMixin,
  ],

  props: {
    handleChangeListItem: {
      required: true,
      type: Function,
    },
    listItem: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      PASSWORD_TR,
      scimAttributeExclusionRadioGroup,
      scimCoreAttributes,
      scimAttribute: this.listItem.target,
      jcAttribute: this.listItem.template,
      jcUiValue: '',
      exclude: this.listItem.exclude,
    };
  },

  computed: {
    ...mapState('ApplicationModel', {
      translationRules: 'scimTranslationRules',
    }),
    // Extract each type of property from constants
    getScimAttributes() {
      return scimCoreAttributes.map(
        attrObject => attrObject.scimAttribute,
      ).sort();
    },
    getUIAttributeNames() {
      return scimCoreAttributes.reduce((uiValuesList, nextAttr) => {
        if (nextAttr.scimAttribute === PASSWORD_TR) {
          return uiValuesList;
        }
        uiValuesList.push(nextAttr.uiValue);
        return uiValuesList;
      }, []).sort();
    },

    getUsedScimAttributes() {
      return this.translationRules.map((rule) => rule.target);
    },

    isJCAttributeDropdownClickable() {
      return { 'pointer-events': this.isUnclickableDropDown.jcAttributeColumn };
    },

    isJcAttributeRequired() {
      return (!this.exclude ? 'required' : '');
    },

    isSCIMAttributeDropdownClickable() {
      return { 'pointer-events': this.isUnclickableDropDown.scimAttributeColumn };
    },

    isUnclickableDropDown() {
      const NONE = 'none';
      const AUTO = 'auto';
      const dropDownClickConfig = {
        scimAttributeColumn: AUTO,
        jcAttributeColumn: AUTO,
      };

      // Only SCIM attribute dropdown is clickable
      if (this.listItem.exclude) {
        dropDownClickConfig.scimAttributeColumn = AUTO;
        dropDownClickConfig.jcAttributeColumn = NONE;
      }

      // None of the fields is clickable
      if (this.listItem.required) {
        dropDownClickConfig.scimAttributeColumn = NONE;
        dropDownClickConfig.jcAttributeColumn = NONE;
      }

      return dropDownClickConfig;
    },

    unusedScimAttributes() {
      const usedScimAttributes = this.getUsedScimAttributes;

      if (usedScimAttributes.length <= 0) {
        return this.getScimAttributes;
      }

      const unusedScimAttributes = [];
      this.getScimAttributes.forEach((attribute) => {
        if (!usedScimAttributes.includes(attribute)) {
          unusedScimAttributes.push(attribute);
        }
      });
      return unusedScimAttributes.sort();
    },
  },

  watch: {
    listItem: {
      handler(newVal, oldValue) {
        if (!isMatch(oldValue, newVal)) {
          const uiValue = this.getUIValueForJcAttribute(newVal.target);
          this.scimAttribute = newVal.target;
          this.jcAttribute = newVal.template;
          this.jcUiValue = uiValue;
          this.exclude = newVal.exclude;
        }
      },
      deep: true,
    },
  },

  created() {
    this.jcUiValue = this.getUIValueForJcAttribute(this.listItem.target);
  },

  methods: {
    /**
     * Returns the UI value equivalent to the JC attribute name
     * Example: name.familyName => Last Name
     * @param {string} scimAttributeName
     * @returns {string}
     */
    getUIValueForJcAttribute(scimAttributeName = '') {
      let uiValue = NOT_APPLICABLE;
      if (this.listItem.readOnly || !this.exclude) {
        const lookupAttribute = scimCoreAttributes.find(
          attrObject => attrObject.scimAttribute === scimAttributeName,
        );

        uiValue = lookupAttribute ? lookupAttribute.uiValue : '';
      }
      return uiValue;
    },
    handleAttributeChange(value, key = 'exclude') {
      this[key] = value;

      // Find matching internal value to scimAttribute
      if (key === 'jcUiValue' && !this.exclude) {
        const internalScimValue = this.scimCoreAttributes
          .find(attrObject => attrObject.uiValue === value).jcAttribute;

        this.jcAttribute = internalScimValue;
      }

      // Attribute is excluded, reset scimAttribute and change UI value
      if (!this.listItem.readOnly && key === 'exclude') {
        this.jcUiValue = '';
        if (this.exclude) {
          this.jcUiValue = NOT_APPLICABLE;
          this.jcAttribute = '';
        }
      }

      // Payload
      const newScimAttributes = {
        target: this.scimAttribute,
        template: this.jcAttribute,
        exclude: this.exclude,
      };

      this.handleChangeListItem(newScimAttributes);
    },
  },
};
</script>

<style module>
@import '../../css/flex.css';
@import './css/IdentityManagementCommon.css';

.container {
  align-items: center;
  display: flex;
  width: 100%;
}

.dropDownsColumn {
  flex: 1 1 30%;
  padding: 0 10px 0 0;
}

.flexCol {
  display: flex;
  flex-direction: column;
}

.toggleButtonsColumn {
  align-self: center;
  flex: 0 1 auto;
  margin-bottom: 0.6rem;
}
</style>
